import React, { useContext, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  Button,
  Grid,
  IconButton,
  Icons,
  Modal,
  Text,
  ProgressBar,
  Loader,
  DateRangeText,
} from "@pushpress/shared-components";
import { PRIMARY_COLOR } from "../../../../constants/colors";
import { ToggleHandlers, useToggle } from "../../../../hooks/state";
import { toast } from "react-toastify";
import { defaultToastOptions, extractError } from "../../../../utils/misc";
import styles from "./QuickEditModal.module.scss";
import {
  useGetCalendarItemForEditQuery,
  GetCalendarItemForEditQuery,
  useUpdateCalendarItemMutation,
} from "./../CalendarForm-generated.hooks";
import CancelCalendarItemModal from "./../CancelCalendarItem";
import { AppContext } from "../../../../context/app";
import { parseQuickEditChanges } from "../CalendarForm.utils";
import StaffProfileSelect from "../../../../components/controllers/StaffProfileSelect";
import { StaffProfileSelectFragment } from "../../../../components/controllers/StaffProfileSelect/StaffProfilesSelect-generated.hooks";
import IconMenu from "./../../components/IconMenu";

type QuickEditProps = {
  uuid?: string;
  onCancel: (uuid: string) => void;
  onEditClick: (
    calendarItem: GetCalendarItemForEditQuery["getCalendarItem"],
  ) => void;
  onDuplicateClick: (
    CalendarItem: GetCalendarItemForEditQuery["getCalendarItem"],
  ) => void;
  onClose: VoidFunction;
  visible: boolean;
  onMainCoachChange?: (
    calendarItemUuid: string,
    coach: { userUuid: string; firstName: string; lastName: string },
  ) => void;
};

type QuickEditForm = {
  mainCoach?: StaffProfileSelectFragment;
  assistantCoach?: StaffProfileSelectFragment;
};

const QuickEditModal = (props: QuickEditProps) => {
  const { visible, onClose } = props;
  const [showDeleteModal, showDeleteModalActions] = useToggle(false);

  return (
    <Modal
      visible={visible}
      closeModal={() => {
        if (!showDeleteModal) onClose();
      }}
      disableClickAway
    >
      <Modal.Card>
        <QuickEditContent
          {...props}
          showDeleteModal={showDeleteModal}
          showDeleteModalActions={showDeleteModalActions}
        />
      </Modal.Card>
    </Modal>
  );
};

const QuickEditContent = ({
  uuid,
  onClose,
  onCancel,
  onEditClick,
  onDuplicateClick,
  showDeleteModal,
  showDeleteModalActions,
  onMainCoachChange,
}: QuickEditProps & {
  showDeleteModal: boolean;
  showDeleteModalActions: ToggleHandlers;
}) => {
  const history = useHistory();
  const { client } = useContext(AppContext);
  const {
    data,
    error,
    loading: loadingGetCalendarItem,
  } = useGetCalendarItemForEditQuery({
    variables: {
      getCalendarItemInput: {
        uuid: uuid || "",
        clientUuid: client.client_uuid || client.uuid,
      },
    },
    skip: !uuid,
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
  });

  const calendarItem = data?.getCalendarItem;
  const { control, setValue } = useForm<QuickEditForm>();
  const [updateCalendarItemMutation] = useUpdateCalendarItemMutation();

  useEffect(() => {
    if (calendarItem) {
      setValue("mainCoach", calendarItem.mainCoach);
      setValue("assistantCoach", calendarItem.assistantCoach);
    }
  }, [setValue, calendarItem]);

  const updateMainCoach = async (coach?: StaffProfileSelectFragment) => {
    if (!calendarItem.uuid) {
      toast.warning("No calendar item", defaultToastOptions);
    }
    try {
      const uuid = coach ? coach.userUuid : null;
      const updateCalendarItemInput = parseQuickEditChanges(
        calendarItem.uuid,
        calendarItem.type,
        {
          coachUuid: uuid,
        },
      );

      await updateCalendarItemMutation({
        variables: {
          updateCalendarItemInput,
        },
      });

      if (uuid) {
        toast.success(
          `${isEvent ? "Staff" : "Coach"} assigned`,
          defaultToastOptions,
        );
      } else {
        toast.success(
          `${isEvent ? "Staff" : "Coach"} removed`,
          defaultToastOptions,
        );
      }
      onMainCoachChange?.(calendarItem.uuid, coach);
    } catch (err) {
      toast.error(extractError(err), defaultToastOptions);
    }
  };

  const updateAssistantCoach = async (assistantCoachUuid?: string) => {
    if (!calendarItem.uuid) {
      toast.warning("No calendar item", defaultToastOptions);
    }

    try {
      const uuid = assistantCoachUuid ? assistantCoachUuid : null;
      const updateCalendarItemInput = parseQuickEditChanges(
        calendarItem.uuid,
        calendarItem.type,
        {
          assistantCoachUuid: uuid,
        },
      );
      await updateCalendarItemMutation({
        variables: {
          updateCalendarItemInput,
        },
      });

      if (assistantCoachUuid) {
        toast.success("Assistant coach assigned", defaultToastOptions);
      } else {
        toast.success("Assistant coach removed", defaultToastOptions);
      }
    } catch (err) {
      toast.error(extractError(err), defaultToastOptions);
    }
  };

  const isLoading = loadingGetCalendarItem && !calendarItem;
  const isEvent = calendarItem?.type === "Event";

  return (
    <>
      {isLoading && (
        <Modal.Content>
          <Grid
            container
            marginY={10}
            justifyContent="center"
            alignItems="center"
          >
            <Grid item>
              <Loader size={20} />
            </Grid>
          </Grid>
        </Modal.Content>
      )}
      {error && !calendarItem && (
        <Modal.Content>
          <Grid
            container
            marginY={10}
            justifyContent="center"
            alignItems="center"
          >
            <Grid item>
              <Text variant="body-lg">{error.message}</Text>
            </Grid>
          </Grid>
        </Modal.Content>
      )}
      {calendarItem && !isLoading && (
        <>
          <CancelCalendarItemModal
            visible={showDeleteModal}
            uuid={calendarItem.uuid}
            onClose={showDeleteModalActions.off}
            onCalendarItemCancelled={() => onCancel(calendarItem.uuid)}
          />
          <Modal.Content>
            <Grid
              container
              justifyContent="flex-end"
              alignItems="center"
              spacing={3}
              marginY={3}
            >
              <Grid item>
                <IconButton
                  icon={Icons.Pencil}
                  onClick={() => {
                    if (calendarItem) {
                      onEditClick(calendarItem);
                    }
                  }}
                  size={20}
                />
              </Grid>
              <Grid item>
                {calendarItem.type === "Class" ? (
                  <IconMenu
                    icon={Icons.Kebabs}
                    menuItems={[
                      {
                        label: "View",
                        onClick: () => history.push(`/schedule/${uuid}`),
                      },
                      {
                        label: "Edit",
                        onClick: () => {
                          if (calendarItem) {
                            onEditClick(calendarItem);
                          }
                        },
                      },
                      {
                        label: "Delete",
                        onClick: showDeleteModalActions.on,
                      },
                      {
                        label: "Duplicate",
                        onClick: () => {
                          if (calendarItem) {
                            onDuplicateClick(calendarItem);
                          }
                        },
                      },
                    ]}
                  />
                ) : (
                  <IconButton
                    icon={Icons.Trash}
                    size={20}
                    onClick={showDeleteModalActions.on}
                  />
                )}
              </Grid>
              <Grid item>
                <IconButton
                  icon={Icons.Close}
                  size={18}
                  onClick={onClose}
                  aria-label="Close"
                />
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={1}>
                <div
                  className={`${styles.square} mt-2`}
                  style={{
                    backgroundColor:
                      calendarItem?.calendarItemType?.color || "",
                  }}
                />
              </Grid>
              <Grid item xs={11}>
                <Text variant="heading-4" color="wolf-800" className="mb-2">
                  {calendarItem.title}
                </Text>
                <DateRangeText
                  start={new Date(calendarItem.startDatetime)}
                  end={new Date(calendarItem.endDatetime)}
                  timeZone="UTC"
                  showTime={!calendarItem.isAllDay}
                  variant="body-lg"
                  color="wolf-600"
                />
              </Grid>
            </Grid>
            <div className={`mt-4`}>
              <StaffProfileSelect
                control={control}
                name="mainCoach"
                label={isEvent ? "Staff" : "Coach"}
                placeholder="— Add —"
                isClearable
                onChange={(option) => updateMainCoach(option)}
                menuPortalTarget={document.body}
                hideDropdownIndicator
              />
              {!isEvent && (
                <StaffProfileSelect
                  label="Assistant Coach"
                  name="assistantCoach"
                  placeholder="— Add —"
                  control={control}
                  className={styles.assistantCoachInput}
                  isClearable
                  onChange={(option) => updateAssistantCoach(option?.userUuid)}
                  menuPortalTarget={document.body}
                  hideDropdownIndicator
                />
              )}
            </div>
          </Modal.Content>
          <Modal.Bottom>
            {Number(calendarItem?.attendanceCap) > 0 && (
              <div className="mb-4">
                <Text variant="body-md" color="wolf-600">
                  Attendees
                </Text>
                <Text variant="body-lg" color="wolf-800">
                  {calendarItem.attendanceCap -
                    Number(calendarItem.spotsAvailable)}{" "}
                  / {calendarItem.attendanceCap}
                </Text>
                <div className={`${styles.progressBar} mt-2`}>
                  <ProgressBar
                    label=""
                    color={PRIMARY_COLOR}
                    progressWidth={
                      ((calendarItem.attendanceCap -
                        Number(calendarItem.spotsAvailable)) *
                        100) /
                      Number(calendarItem.attendanceCap)
                    }
                  />
                </div>
              </div>
            )}
            <Button
              component={Link}
              buttonType="secondary"
              text={`View ${calendarItem.type?.toLowerCase()}`}
              icon={Icons.ArrowRight}
              iconLocation="right"
              fullWidth
              size="large"
              to={`/schedule/${uuid}`}
            />
            {calendarItem.type === "Event" && (
              <Button
                component="a"
                buttonType="secondary"
                text="Landing page"
                icon={Icons.NewPage}
                iconLocation="right"
                fullWidth
                size="large"
                className="mt-3"
                target="_blank"
                href={`/landing/events/${uuid}`}
              />
            )}
          </Modal.Bottom>
        </>
      )}
    </>
  );
};

export default QuickEditModal;
